// Helpers for the fast-burst alerts module (state.burst).

export const DAY_MS = 24 * 60 * 60 * 1000;

// Accepts epoch millis (number or numeric string), a Date, or a parseable date
// string. Returns NaN for anything else.
export function toEpochMs(value) {
  if (value == null || value === '') {
    return NaN;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (value instanceof Date) {
    return value.getTime();
  }
  const asNumber = Number(value);
  if (!Number.isNaN(asNumber)) {
    return asNumber;
  }
  return Date.parse(value);
}

// Span of readings a noise-graph row covers, ending at its TimeStamp. The
// device reports `num_samples` readings taken every `interval_samples`
// minutes; when those are missing assume the usual daily report.
export function noiseGraphWindowMs(row) {
  const samples = Number(row?.num_samples);
  const interval = Number(row?.interval_samples);
  if (samples > 0 && interval > 0) {
    return samples * interval * 60 * 1000;
  }
  return DAY_MS;
}

// Flattens one or more noiseGraph responses into a single list, dropping rows
// without a usable TimeStamp and duplicates (same TimeStamp), oldest first.
export function mergeNoiseGraphRows(...responses) {
  const byTimeStamp = new Map();
  responses.forEach((rows) => {
    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const ts = toEpochMs(row?.TimeStamp);
      if (Number.isFinite(ts) && !byTimeStamp.has(ts)) {
        byTimeStamp.set(ts, row);
      }
    });
  });
  return Array.from(byTimeStamp.entries())
    .sort((a, b) => a[0] - b[0])
    .map((entry) => entry[1]);
}

// Picks the noise-graph row that belongs to a burst sample.
//
// A graph stamped T holds the readings of the ~24h *before* T (the device
// reports it once a day, at night). So the graph received on the 30th at night
// describes the 29th, and a sample from the 29th must be shown with it — not
// with the graph stamped the 29th, which describes the 28th.
//
// Preference order:
//   1. a row whose covered window contains the sample time (earliest such row);
//   2. the first row received after the sample (the next nightly report);
//   3. the latest row available.
export function pickNoiseGraphRowForSample(rows, sampleTime) {
  const sorted = mergeNoiseGraphRows(rows);
  if (sorted.length === 0) {
    return undefined;
  }
  const sampleMs = toEpochMs(sampleTime);
  if (!Number.isFinite(sampleMs)) {
    return sorted[sorted.length - 1];
  }

  const containing = sorted.find((row) => {
    const end = toEpochMs(row.TimeStamp);
    const start = end - noiseGraphWindowMs(row);
    return sampleMs >= start && sampleMs <= end;
  });
  if (containing) {
    return containing;
  }

  const after = sorted.find((row) => toEpochMs(row.TimeStamp) >= sampleMs);
  if (after) {
    return after;
  }

  return sorted[sorted.length - 1];
}

// Default status filter for the burst alerts table: New, To Be Located (TBL)
// and Monitoring. The numeric values below are the fallback used until the
// AlertStateNoise option list is loaded; once it is, the values are resolved
// by option name so the filter tracks whatever the server assigns.
export const BURST_DEFAULT_ALERT_STATES = [1, 2, 3];

const BURST_DEFAULT_ALERT_STATE_NAMES = new Set([
  'new',
  'tobelocated',
  'tbl',
  'monitoring',
]);

const normalizeOptionName = (name) =>
  String(name == null ? '' : name)
    .toLowerCase()
    .replace(/[^a-z]/g, '');

export function resolveBurstDefaultAlertStates(alertStateOptions) {
  if (!Array.isArray(alertStateOptions) || alertStateOptions.length === 0) {
    return [...BURST_DEFAULT_ALERT_STATES];
  }
  const matched = [];
  alertStateOptions.forEach((option) => {
    if (option == null || option.value == null) {
      return;
    }
    // DisplayName is localized (options are fetched per language); try every
    // name-like field so an English `Name` still matches under other locales.
    const isWanted = [option.Name, option.DisplayName, option.label].some(
      (candidate) =>
        candidate != null &&
        BURST_DEFAULT_ALERT_STATE_NAMES.has(normalizeOptionName(candidate))
    );
    if (isWanted && matched.indexOf(option.value) === -1) {
      matched.push(option.value);
    }
  });
  // Only trust the name lookup when it found all three states; a partial match
  // means the names differ from what we expect, so keep the known defaults.
  return matched.length >= 3 ? matched : [...BURST_DEFAULT_ALERT_STATES];
}
